import React, { FC, useMemo, useState, useRef, useEffect } from 'react';
import config from '../../../config';
import { fetchFeatureFlags } from '../../../services/fetchFeatureFlags';
import { SalesTableRow, ParticipantStatus } from './types';

interface ParticipantSalesTableProps {
  rows: SalesTableRow[];
  showDivision?: boolean;
  groupByTeamAdmin?: boolean;
  pageSize?: number;
  fundraiserId?: number;
}

const PAGE_SIZE = 25;

const BRAND = '#163172';

const avatarColor = (name: string): string => {
  const colors = [BRAND, '#0891b2', '#059669', '#7c3aed', '#db2777', '#d97706', '#dc2626'];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return colors[Math.abs(h) % colors.length];
};

const initials = (name: string) => {
  const p = name.trim().split(' ');
  if (p.length === 1) return p[0][0]?.toUpperCase() ?? '?';
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
};

const fmt = (n: number) =>
  `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

const storefrontUrl = (code: string) =>
  `/participant-shop/part-id/${code}`;

const storefrontFullUrl = (code: string) =>
  `${window.location.origin}/participant-shop/part-id/${code}`;

const statusStyle = (s?: ParticipantStatus): { bg: string; color: string; dot: string } => {
  switch (s) {
    case 'Active':   return { bg: '#dcfce7', color: '#15803d', dot: '#22c55e' };
    case 'Slow':     return { bg: '#fef9c3', color: '#a16207', dot: '#eab308' };
    case 'Inactive': return { bg: '#fee2e2', color: '#b91c1c', dot: '#ef4444' };
    default:         return { bg: '#f1f5f9', color: '#64748b', dot: '#94a3b8' };
  }
};

const CopyIcon = () => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
  </svg>
);

const ExternalIcon = () => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

const DotsIcon = () => (
  <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
    <circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" />
  </svg>
);

const BellIcon = () => (
  <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

type ReminderStatus = 'idle' | 'confirm' | 'sending' | 'sent' | 'error' | 'cooldown';

// Overflow menu shown on mobile (< 576px via CSS d-flex d-sm-none)
const MobileActionsMenu: FC<{
  url: string;
  fullUrl: string;
  participantEmail?: string | null;
  onSendReminder?: () => Promise<ReminderStatus>;
}> = ({ url, fullUrl, participantEmail, onSendReminder }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reminderStatus, setReminderStatus] = useState<ReminderStatus>('idle');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const handleCopy = () => {
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); setOpen(false); }, 1500);
    });
  };

  const handleRemind = async () => {
    if (!onSendReminder || reminderStatus === 'sending' || reminderStatus === 'sent') return;
    if (reminderStatus === 'idle') {
      setReminderStatus('confirm');
      return;
    }
    // 'confirm' state — user clicked a second time, proceed
    setReminderStatus('sending');
    const result = await onSendReminder();
    setReminderStatus(result);
    if (result === 'sent') setTimeout(() => { setReminderStatus('idle'); setOpen(false); }, 1800);
    else if (result === 'error' || result === 'cooldown') setTimeout(() => setReminderStatus('idle'), 3000);
  };

  const reminderLabel = reminderStatus === 'confirm'
    ? `Send to: ${participantEmail ?? 'participant'}`
    : {
        idle: 'Send reminder',
        sending: 'Sending…',
        sent: 'Reminder sent!',
        error: 'Failed — retry',
        cooldown: 'Already sent today',
      }[reminderStatus as Exclude<ReminderStatus, 'confirm'>];

  const reminderColor = reminderStatus === 'sent' ? '#059669'
    : reminderStatus === 'error' ? '#b91c1c'
    : reminderStatus === 'cooldown' ? '#a16207'
    : reminderStatus === 'confirm' ? '#d97706'
    : '#64748b';

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: 36, height: 36,
          border: '1px solid #e2e8f0',
          borderRadius: 8,
          background: open ? '#f0f3ff' : '#fff',
          color: open ? BRAND : '#64748b',
          cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 0,
        }}
        title="Actions"
        aria-label="Row actions"
        aria-expanded={open}
      >
        <DotsIcon />
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 40, zIndex: 200,
          background: '#fff', borderRadius: 10,
          boxShadow: '0 4px 20px rgba(0,0,0,0.14)',
          border: '1px solid #e8edf5',
          minWidth: 160, overflow: 'hidden',
        }}>
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={() => setOpen(false)}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '11px 14px', fontSize: 13, color: BRAND,
              textDecoration: 'none', borderBottom: '1px solid #f0f0f0',
              fontWeight: 500,
            }}
          >
            <ExternalIcon /> Open store
          </a>
          <button
            onClick={handleCopy}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              padding: '11px 14px', fontSize: 13,
              color: copied ? '#059669' : '#64748b',
              background: 'transparent', border: 'none', borderBottom: onSendReminder ? '1px solid #f0f0f0' : 'none',
              cursor: 'pointer', fontWeight: 500,
            }}
          >
            <CopyIcon /> {copied ? 'Copied!' : 'Copy link'}
          </button>
          {onSendReminder && (
            <button
              onClick={handleRemind}
              disabled={reminderStatus === 'sending' || reminderStatus === 'sent'}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                padding: '11px 14px', fontSize: 13,
                color: reminderColor,
                background: 'transparent', border: 'none',
                cursor: reminderStatus === 'sending' || reminderStatus === 'sent' ? 'default' : 'pointer',
                fontWeight: 500,
                opacity: reminderStatus === 'sending' ? 0.6 : 1,
              }}
            >
              <BellIcon /> {reminderLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const ParticipantRow: FC<{
  row: SalesTableRow;
  rank: number;
  maxSales: number;
  showDivision: boolean;
  fundraiserId?: number;
  reminderEnabled?: boolean;
}> = ({ row, rank, maxSales, showDivision, fundraiserId, reminderEnabled }) => {
  const [copied, setCopied] = useState(false);
  const [reminderStatus, setReminderStatus] = useState<ReminderStatus>('idle');
  const confirmTimeout = useRef<ReturnType<typeof setTimeout>>();
  const bar = Math.round((row.total_sales / Math.max(maxSales, 1)) * 100);
  const ss = statusStyle(row.status);
  const url = storefrontUrl(row.participant_code);
  const fullUrl = storefrontFullUrl(row.participant_code);

  const handleCopy = () => {
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSendReminder = async (): Promise<ReminderStatus> => {
    if (!fundraiserId) return 'error';
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(
        `${config.API_BASE_URL}dashboards/send-participant-reminder/`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ participant_code: row.participant_code, fundraiser_id: fundraiserId }),
        }
      );
      if (res.status === 429) return 'cooldown';
      if (!res.ok) return 'error';
      return 'sent';
    } catch {
      return 'error';
    }
  };

  const desktopReminderLabel = reminderStatus === 'sent' ? 'Sent!'
    : reminderStatus === 'cooldown' ? 'Sent today'
    : reminderStatus === 'error' ? 'Error'
    : reminderStatus === 'sending' ? '…'
    : reminderStatus === 'confirm' ? 'Confirm?'
    : 'Remind';

  const desktopReminderColor = reminderStatus === 'sent' ? '#059669'
    : reminderStatus === 'error' ? '#b91c1c'
    : reminderStatus === 'cooldown' ? '#a16207'
    : reminderStatus === 'confirm' ? '#d97706'
    : '#64748b';

  return (
    <tr style={{ borderBottom: '1px solid #fafafa' }}>
      <td className="py-3 ps-0 align-middle border-0">
        <span className="text-muted" style={{ fontSize: 12 }}>{rank}</span>
      </td>
      <td className="py-3 align-middle border-0">
        <div className="d-flex align-items-center gap-2">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle fw-bold text-white flex-shrink-0"
            style={{ width: 32, height: 32, fontSize: 12, background: avatarColor(row.participant_name) }}
          >
            {initials(row.participant_name)}
          </div>
          <div style={{ minWidth: 0 }}>
            <p className="fw-semibold mb-0" style={{ fontSize: 13, color: '#1a1a2e' }}>
              {row.participant_name}
            </p>
            <p className="text-muted mb-0" style={{ fontSize: 11, fontFamily: 'monospace' }}>
              {row.participant_code}
            </p>
          </div>
        </div>
      </td>

      {showDivision && (
        <td className="py-3 align-middle border-0 d-none d-md-table-cell">
          <span className="badge fw-normal" style={{ background: '#f1f5f9', color: '#64748b', fontSize: 11 }}>
            {row.secondary_div || '—'}
          </span>
          {row.primary_div && row.primary_div !== 'N/A' && (
            <p className="mb-0 mt-1 text-muted" style={{ fontSize: 10 }}>{row.primary_div}</p>
          )}
        </td>
      )}

      {row.status && (
        <td className="py-3 align-middle border-0 d-none d-sm-table-cell">
          <span
            className="badge rounded-pill fw-semibold"
            style={{ fontSize: 11, padding: '3px 9px', background: ss.bg, color: ss.color }}
          >
            <span
              className="rounded-circle d-inline-block me-1"
              style={{ width: 6, height: 6, background: ss.dot, verticalAlign: 'middle' }}
            />
            {row.status}
          </span>
        </td>
      )}

      <td className="py-3 align-middle border-0">
        <p className="fw-bold mb-1" style={{ fontSize: 13, color: '#1a1a2e' }}>{fmt(row.total_sales)}</p>
        <div className="rounded-pill overflow-hidden" style={{ height: 3, width: 60, background: '#f1f5f9' }}>
          <div style={{ height: '100%', width: `${bar}%`, background: BRAND, borderRadius: 3 }} />
        </div>
      </td>

      <td className="py-3 align-middle border-0 d-none d-sm-table-cell">
        <span className="fw-semibold" style={{ fontSize: 13, color: '#059669' }}>{fmt(row.total_profit)}</span>
      </td>

      <td className="py-3 align-middle border-0 d-none d-sm-table-cell" style={{ fontSize: 13, color: '#475569' }}>
        {row.num_items_sold}
      </td>

      {/* Actions — inline buttons on ≥576px, ··· overflow menu on mobile */}
      <td className="py-3 align-middle border-0">
        {/* Desktop / tablet: inline Store + Copy + Remind */}
        <div className="d-none d-sm-flex align-items-center gap-1">
          {reminderStatus === 'confirm' ? (
            /* Inline confirmation panel replaces all action buttons */
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#fffbeb', border: '1px solid #d97706',
              borderRadius: 6, padding: '4px 8px',
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                <span style={{ fontSize: 10, color: '#92400e', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Send reminder to:
                </span>
                <span
                  style={{ fontSize: 11, color: '#1a1a2e', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  title={row.participant_email ?? undefined}
                >
                  {row.participant_email ?? row.participant_name}
                </span>
              </div>
              <button
                onClick={async () => {
                  clearTimeout(confirmTimeout.current);
                  setReminderStatus('sending');
                  const result = await handleSendReminder();
                  setReminderStatus(result);
                  if (result !== 'idle') setTimeout(() => setReminderStatus('idle'), 2500);
                }}
                style={{ fontSize: 11, padding: '3px 8px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                Send ✓
              </button>
              <button
                onClick={() => { clearTimeout(confirmTimeout.current); setReminderStatus('idle'); }}
                style={{ fontSize: 13, padding: '2px 5px', background: 'transparent', color: '#92400e', border: 'none', cursor: 'pointer', lineHeight: 1 }}
                title="Cancel"
              >
                ✕
              </button>
            </div>
          ) : (
            <>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm d-flex align-items-center gap-1"
                style={{ fontSize: 11, padding: '5px 10px', border: `1px solid ${BRAND}`, color: BRAND, background: 'transparent' }}
                title="Open storefront"
              >
                <ExternalIcon />
                <span className="d-none d-lg-inline">Store</span>
              </a>
              <button
                onClick={handleCopy}
                className="btn btn-sm d-flex align-items-center gap-1"
                style={{ fontSize: 11, padding: '5px 10px', border: '1px solid #e2e8f0', color: copied ? '#059669' : '#64748b', background: 'transparent' }}
                title="Copy store link"
              >
                <CopyIcon />
                <span className="d-none d-lg-inline">{copied ? 'Copied!' : 'Copy'}</span>
              </button>
              {fundraiserId && reminderEnabled && (
                <button
                  onClick={() => {
                    if (reminderStatus === 'sending' || reminderStatus === 'sent') return;
                    clearTimeout(confirmTimeout.current);
                    setReminderStatus('confirm');
                    confirmTimeout.current = setTimeout(() => setReminderStatus('idle'), 5000);
                  }}
                  disabled={reminderStatus === 'sending' || reminderStatus === 'sent'}
                  className="btn btn-sm d-flex align-items-center gap-1"
                  style={{
                    fontSize: 11, padding: '5px 10px',
                    border: '1px solid #e2e8f0',
                    color: desktopReminderColor,
                    background: 'transparent',
                    opacity: reminderStatus === 'sending' ? 0.6 : 1,
                  }}
                  title="Send reminder email to participant"
                >
                  <BellIcon />
                  <span className="d-none d-xl-inline">{desktopReminderLabel}</span>
                </button>
              )}
            </>
          )}
        </div>
        {/* Mobile: ··· overflow menu with all three actions */}
        <div className="d-flex d-sm-none">
          <MobileActionsMenu
            url={url}
            fullUrl={fullUrl}
            participantEmail={row.participant_email}
            onSendReminder={fundraiserId && reminderEnabled ? handleSendReminder : undefined}
          />
        </div>
      </td>
    </tr>
  );
};

const TeamAdminGroup: FC<{ name: string | null; rows: SalesTableRow[]; maxSales: number; showDivision: boolean; startRank: number; fundraiserId?: number; reminderEnabled?: boolean }> = ({
  name, rows, maxSales, showDivision, startRank, fundraiserId, reminderEnabled,
}) => (
  <>
    <tr>
      <td
        colSpan={showDivision ? 8 : 7}
        style={{ background: '#f8f9ff', padding: '8px 12px', fontSize: 12, fontWeight: 700, color: BRAND, letterSpacing: '0.04em', borderBottom: `2px solid ${BRAND}20` }}
      >
        {name ? `Team Admin: ${name}` : 'Unassigned'}
        <span className="ms-2 fw-normal text-muted" style={{ fontSize: 11 }}>
          ({rows.length} participant{rows.length !== 1 ? 's' : ''})
        </span>
      </td>
    </tr>
    {rows.map((row, i) => (
      <ParticipantRow key={`${row.participant_code}-${i}`} row={row} rank={startRank + i} maxSales={maxSales} showDivision={showDivision} fundraiserId={fundraiserId} reminderEnabled={reminderEnabled} />
    ))}
  </>
);

const ParticipantSalesTable: FC<ParticipantSalesTableProps> = ({
  rows,
  showDivision = true,
  groupByTeamAdmin = false,
  fundraiserId,
}) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ParticipantStatus | 'All'>('All');
  const [page, setPage] = useState(1);
  const [reminderEnabled, setReminderEnabled] = useState(false);

  useEffect(() => {
    fetchFeatureFlags().then(flags => setReminderEnabled(!!flags.send_participant_reminder_emails));
  }, []);

  const hasStatus = rows.some(r => r.status);

  const filtered = useMemo(() => {
    let r = rows;
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(p =>
        p.participant_name.toLowerCase().includes(q) ||
        p.participant_code.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'All') {
      r = r.filter(p => p.status === statusFilter);
    }
    return r;
  }, [rows, search, statusFilter]);

  const maxSales = useMemo(() => Math.max(...filtered.map(r => r.total_sales), 1), [filtered]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = groupByTeamAdmin ? filtered : filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSearch = (v: string) => { setSearch(v); setPage(1); };
  const handleStatus = (v: ParticipantStatus | 'All') => { setStatusFilter(v); setPage(1); };

  if (!rows || rows.length === 0) {
    return (
      <div className="text-center py-5">
        <div style={{ fontSize: 36, opacity: 0.3, marginBottom: 8 }}>👥</div>
        <p className="text-muted small mb-0">No participants yet</p>
      </div>
    );
  }

  return (
    <div>
      {/* Search + filter bar */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <div className="flex-grow-1" style={{ minWidth: 180, maxWidth: 320 }}>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search by name or code…"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            style={{ fontSize: 13 }}
          />
        </div>
        {hasStatus && (
          <div className="d-flex gap-1 flex-wrap">
            {(['All', 'Active', 'Slow', 'Inactive'] as const).map(s => {
              const active = statusFilter === s;
              const ss = s !== 'All' ? statusStyle(s as ParticipantStatus) : null;
              return (
                <button
                  key={s}
                  onClick={() => handleStatus(s as ParticipantStatus | 'All')}
                  className="btn btn-sm fw-semibold"
                  style={{
                    fontSize: 11,
                    padding: '3px 10px',
                    borderRadius: 20,
                    border: `1px solid ${active ? (ss?.dot || BRAND) : '#e2e8f0'}`,
                    background: active ? (ss?.bg || '#f0f3ff') : '#fff',
                    color: active ? (ss?.color || BRAND) : '#64748b',
                  }}
                >
                  {s}
                </button>
              );
            })}
          </div>
        )}
        <span className="text-muted ms-auto" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover mb-0">
          <thead>
            <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
              <th className="pb-3 ps-0 border-0" style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', width: 32 }}>#</th>
              <th className="pb-3 border-0" style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Participant</th>
              {showDivision && (
                <th className="pb-3 border-0 d-none d-md-table-cell" style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Team</th>
              )}
              {hasStatus && (
                <th className="pb-3 border-0 d-none d-sm-table-cell" style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Status</th>
              )}
              <th className="pb-3 border-0" style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sales</th>
              <th className="pb-3 border-0 d-none d-sm-table-cell" style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Profit</th>
              <th className="pb-3 border-0 d-none d-sm-table-cell" style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Items</th>
              <th className="pb-3 border-0" style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {groupByTeamAdmin ? (
              (() => {
                const groups: Record<string, SalesTableRow[]> = {};
                for (const row of paged) {
                  const key = row.team_admin_name || '__unassigned__';
                  if (!groups[key]) groups[key] = [];
                  groups[key].push(row);
                }
                let rankCounter = 1;
                return Object.entries(groups).map(([key, groupRows]) => {
                  const name = key === '__unassigned__' ? null : key;
                  const el = (
                    <TeamAdminGroup
                      key={key}
                      name={name}
                      rows={groupRows}
                      maxSales={maxSales}
                      showDivision={showDivision}
                      startRank={rankCounter}
                      fundraiserId={fundraiserId}
                      reminderEnabled={reminderEnabled}
                    />
                  );
                  rankCounter += groupRows.length;
                  return el;
                });
              })()
            ) : (
              paged.map((row, i) => (
                <ParticipantRow
                  key={`${row.participant_code}-${i}`}
                  row={row}
                  rank={(page - 1) * PAGE_SIZE + i + 1}
                  maxSales={maxSales}
                  showDivision={showDivision}
                  fundraiserId={fundraiserId}
                  reminderEnabled={reminderEnabled}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!groupByTeamAdmin && totalPages > 1 && (
        <div className="d-flex align-items-center justify-content-between mt-4 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
          <span className="text-muted small">{filtered.length} participants</span>
          <div className="d-flex align-items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="btn btn-sm fw-medium"
              style={{ fontSize: 12, border: '1px solid #e2e8f0', color: '#475569', background: '#fff' }}
            >
              ← Prev
            </button>
            <span className="small text-muted">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="btn btn-sm fw-medium"
              style={{ fontSize: 12, border: '1px solid #e2e8f0', color: '#475569', background: '#fff' }}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParticipantSalesTable;
